import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, MinusCircle, Circle, ExternalLink, Coins, Timer, Play, AlertTriangle } from 'lucide-react';
import { WorkflowRun, WorkflowStep, WorkflowInput, TraceStatus, logoUrl } from './workflowData';
interface Props {
  runs: WorkflowRun[];
  steps: WorkflowStep[];
  inputs: WorkflowInput[];
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
}
export function WorkflowRunsTab({
  runs,
  steps,
  inputs,
  selectedRunId,
  onSelectRun
}: Props) {
  const selected = runs.find(r => r.id === selectedRunId) ?? runs[0];
  return <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6" data-id="element-5999" data-mp="1f6m1dxskx23">
      {/* Run list */}
      <div data-id="element-6000" data-mp="3slhlj5n5lprb">
        <div className="flex items-center justify-between mb-3" data-id="element-6001" data-mp="3slzmugooau63">
          <h2 className="font-title text-[17px] font-bold" data-id="element-6002" data-mp="3nlld8u7fbc5y">Runs</h2>
          <span className="font-mono text-[11px] text-neutral-500" data-id="element-6003" data-mp="3nn6bouwuzf93">
            {runs.length} recorded
          </span>
        </div>
        <ul className="space-y-2" data-id="element-6004" data-mp="1i17ul4quou0s">
          {runs.map(run => {
          const active = selected?.id === run.id;
          return <li key={run.id} data-id="element-6005" data-mp="1msznvn1e5dum">
                <button type="button" onClick={() => onSelectRun(run.id)} aria-current={active ? 'true' : undefined} className={`w-full text-left rounded-2xl border p-3.5 transition-all ${active ? 'border-[#2B211A] bg-white shadow-sm' : 'border-[#E7E3DD] bg-white hover:border-[#C9C2B8]'}`} data-id="element-6006" data-mp="1mu10hvy228eg">
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-6007" data-mp="20pwblfm7qf7a">
                    <StatusIcon status={run.status} data-id="element-6008" data-mp="25pr57qxdhiw1" />
                    <span className="text-[13.5px] font-bold flex-1 truncate" data-id="element-6009" data-mp="1qtt9g9rd2gkc">
                      {run.trigger}
                    </span>
                    <span className="font-mono text-[11px] text-neutral-500 shrink-0" data-id="element-6010" data-mp="1vrl3pafhaerp">
                      {run.duration}
                    </span>
                  </div>
                  <p className="text-[12px] text-neutral-500 truncate" data-id="element-6011" data-mp="ke8xisw8xk2d">
                    {run.actor} · {run.relative}
                  </p>
                </button>
              </li>;
        })}
        </ul>
      </div>

      {/* Run detail */}
      {selected ? <motion.section key={selected.id} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24
    }} className="rounded-2xl border border-[#E7E3DD] bg-white overflow-hidden" aria-label="Run detail" data-id="element-6012" data-mp="2i3ecpubxe2tg">
          <header className="px-5 py-4 border-b border-[#EFEBE5]" data-id="element-6013" data-mp="23dyp2n7oydhz">
            <div className="flex flex-wrap items-center gap-3" data-id="element-6014" data-mp="1zaadzujuozrz">
              <StatusBadge status={selected.status} data-id="element-6015" data-mp="1zd6d3m7fpui0" />
              <div className="flex-1 min-w-0" data-id="element-6016" data-mp="1ucidic5545c6">
                <h3 className="font-title text-[16px] font-bold truncate" data-id="element-6017" data-mp="1udjz3ajs8a85">
                  {selected.trigger} run · {selected.startedAt}
                </h3>
                <p className="text-[12.5px] text-neutral-500 truncate" data-id="element-6018" data-mp="3op87suzf7gk3">
                  Triggered by {selected.actor}
                </p>
              </div>
              <div className="flex items-center gap-4 font-mono text-[11.5px] text-neutral-500" data-id="element-6019" data-mp="3rapqtymxk172">
                <span className="inline-flex items-center gap-1.5" data-id="element-6020" data-mp="3lj11iyoaxl">
                  <Timer size={13} data-id="element-6021" data-mp="1sfvw7k48uc" />
                  {selected.duration}
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-6022" data-mp="4wog3jmy62ij">
                  <Coins size={13} data-id="element-6023" data-mp="9sdbdarxox91" />
                  {selected.credits} credits
                </span>
              </div>
            </div>

            <div className="mt-3.5 rounded-xl bg-[#F6F5F3] border border-[#EFEBE5] px-3.5 py-2.5" data-id="element-6024" data-mp="38dogdyalx8vq">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400 mb-1.5" data-id="element-6025" data-mp="3i9hsxhobt2di">
                Inputs
              </p>
              <dl className="space-y-1.5" data-id="element-6026" data-mp="3n9m916lbnv37">
                {inputs.map(input => {
              const value = selected.inputValues[input.key];
              return <div key={input.id} className="flex flex-wrap items-baseline gap-x-2" data-id="element-6027" data-mp="xlllim6284tv">
                      <dt className="text-[12.5px] font-semibold text-[#5C544C] shrink-0" data-id="element-6028" data-mp="12kgdadm093q4">
                        {input.label}
                      </dt>
                      <dd className="font-mono text-[12.5px] text-[#2B211A] break-all min-w-0" data-id="element-6029" data-mp="w3567hc2jiou">
                        {value ? value : <span className="text-neutral-400" data-id="element-6030" data-mp="1a3fymot115c4">not provided</span>}
                      </dd>
                    </div>;
            })}
              </dl>
            </div>
          </header>

          <div className="px-5 py-5" data-id="element-6031" data-mp="1j18crpn0i6xx">
            <ol className="relative space-y-4" data-id="element-6032" data-mp="1j06up17kwk6p">
              {selected.traces.map((t, i) => {
            const step = steps.find(s => s.id === t.stepId);
            const last = i === selected.traces.length - 1;
            return <li key={t.stepId} className="relative pl-9" data-id="element-6033" data-mp="1nsrinm3t032b" data-mpi={i}>
                    {!last && <span aria-hidden="true" className={`absolute left-[11px] top-7 bottom-[-18px] w-px ${t.status === 'success' ? 'bg-emerald-200' : 'bg-[#E7E3DD]'}`} data-id="element-6034" data-mp="1nvm4ni2gtvxu" />}
                    <span className="absolute left-0 top-0.5" data-id="element-6035" data-mp="1xt0gf4dg1wjt">
                      <StatusIcon status={t.status} data-id="element-6036" data-mp="2hczg9gwmi5st" />
                    </span>
                    <div className="flex items-start gap-2.5" data-id="element-6037" data-mp="2leklqh6tvhsz">
                      <div className="flex-1 min-w-0" data-id="element-6038" data-mp="2lcqcw18tzg59">
                        <h4 className={`text-[14px] font-bold ${t.status === 'pending' || t.status === 'skipped' ? 'text-neutral-400' : ''}`} data-id="element-6039" data-mp="27fuon1k0di4m">
                          {step?.title ?? `Step ${i + 1}`}
                        </h4>
                        <p className={`mt-0.5 text-[13px] ${t.status === 'failed' ? 'text-red-600' : 'text-[#5C544C]'}`} data-id="element-6040" data-mp="1xnownrtrb0pf">
                          {t.detail}
                        </p>
                        {t.log.length > 0 && <ul className="mt-2 rounded-xl bg-[#FBFAF8] border border-[#EFEBE5] px-3 py-2 space-y-1" data-id="element-6041" data-mp="1oqel05d119jz">
                            {t.log.map(line => <li key={line} className="font-mono text-[11.5px] text-neutral-500 break-words" data-id="element-6042" data-mp="1eqntmcjrjao4">
                                {line}
                              </li>)}
                          </ul>}
                      </div>
                      <span className="font-mono text-[11px] text-neutral-400 shrink-0 pt-0.5" data-id="element-6043" data-mp="10lnh0gpmgryp">
                        {t.duration}
                      </span>
                    </div>
                  </li>;
          })}
            </ol>

            {selected.status === 'running' && <p className="mt-5 flex items-center gap-2 text-[13px] font-semibold text-[#EA580C]" data-id="element-6044" data-mp="lwhhssqsb1qt">
                <Loader2 size={14} className="animate-spin" data-id="element-6045" data-mp="1jjzs4803gbwx" />
                Run in progress…
              </p>}

            {selected.failureReason && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex gap-2.5" data-id="element-6046" data-mp="1elepy31a7i5u">
                <AlertTriangle size={15} className="text-red-600 shrink-0 mt-0.5" data-id="element-6047" data-mp="19jpfaohz16ia" />
                <div data-id="element-6048" data-mp="14lxmls9i5gd1">
                  <p className="text-[13px] font-bold text-red-800" data-id="element-6049" data-mp="14ojrtl8x7js3">
                    Run failed
                  </p>
                  <p className="text-[13px] text-red-700 leading-relaxed" data-id="element-6050" data-mp="3pcoeugep20qm">
                    {selected.failureReason}
                  </p>
                </div>
              </div>}

            {selected.output && <a href="#" className="mt-5 flex items-center gap-3 rounded-xl border border-[#E7E3DD] bg-[#FFFCF8] px-4 py-3 hover:border-[#EA580C] transition-colors group" data-id="element-6051" data-mp="29th0c759oewd">
                <img src={logoUrl('notion.so')} alt="" className="w-5 h-5 rounded object-contain" data-id="element-6052" data-mp="2eqqm9ctg68fg" />
                <span className="flex-1 min-w-0" data-id="element-6053" data-mp="2jlmqrcfveo9g">
                  <span className="block text-[13px] font-bold" data-id="element-6054" data-mp="2ojeju9s94gf8">Output</span>
                  <span className="block font-mono text-[12px] text-neutral-500 truncate" data-id="element-6055" data-mp="2sjyen6pcr7y7">
                    {selected.output}
                  </span>
                </span>
                <ExternalLink size={14} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-6056" data-mp="2xhi4l3vex95z" />
              </a>}
          </div>
        </motion.section> : <div className="rounded-2xl border-2 border-dashed border-[#E0DBD3] py-20 text-center" data-id="element-6057" data-mp="136uwetz5z6xi">
          <Play size={20} className="mx-auto mb-2 text-neutral-300" data-id="element-6058" data-mp="18569ddkr9xjy" />
          <p className="text-[13.5px] text-neutral-500" data-id="element-6059" data-mp="3a1f4lho8jmoq">
            No recorded runs yet. Use Run now to create one.
          </p>
        </div>}
    </div>;
}
function StatusIcon({
  status
}: {
  status: TraceStatus | 'success' | 'failed' | 'running';
}) {
  if (status === 'success') return <CheckCircle2 size={17} className="text-emerald-600 shrink-0" data-id="element-6060" data-mp="3esx5k5q9dn9p" />;
  if (status === 'failed') return <XCircle size={17} className="text-red-600 shrink-0" data-id="element-6061" data-mp="2r2sxnm61f1cw" />;
  if (status === 'running') return <Loader2 size={17} className="text-[#EA580C] animate-spin shrink-0" data-id="element-6062" data-mp="2m3zo24tvfui0" />;
  if (status === 'skipped') return <MinusCircle size={17} className="text-neutral-300 shrink-0" data-id="element-6063" data-mp="310z2ocn54nlz" />;
  return <Circle size={17} className="text-neutral-300 shrink-0" data-id="element-6064" data-mp="30z5uuu0sjxce" />;
}
function StatusBadge({
  status
}: {
  status: 'success' | 'failed' | 'running';
}) {
  const map = {
    success: {
      label: 'Succeeded',
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    failed: {
      label: 'Failed',
      cls: 'bg-red-50 text-red-700 border-red-200'
    },
    running: {
      label: 'Running',
      cls: 'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]'
    }
  }[status];
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-bold ${map.cls}`} data-id="element-6065" data-mp="nsfy8jbtbx1h">
      <StatusIcon status={status} data-id="element-6066" data-mp="dw34xmk6jl79" />
      {map.label}
    </span>;
}